import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { LENDER_CHANNELS_API, LenderChannelOption } from '../tokens/lender-channels.token';
import {
  InvitedSme,
  LenderProspectsApi,
  PreApprovalState,
  ProspectProposal,
  ProspectVerdictRequest,
  ProspectVerdictResponse,
} from '../tokens/lender-prospects.token';

// ── Backend wire shapes ───────────────────────────────────────────────────────

/** {@code GET /api/v1/lender/prospects} row — B2 {@code ProspectQueueItemDTO}. */
interface ProspectQueueItemDto {
  preApprovalId: number;
  cui: string;
  sme: string;
  partner: string | null;
  industry: string | null;
  volume: number | null;
  payTiming: string | null;
  paySize: string | null;
  paymentTermDays: number | null;
  averageBasketValue: number | null;
  kycPre: string | null;
  grade: string | null;
  risk: string | null;
  nativeGrade: string | null;
  state: PreApprovalState;
  cap: number | null;
  pricingDraft: string | null;
  reason: string | null;
  infoRequest: string | null;
  submitted: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

/** {@code GET /api/v1/lender/portfolio} row — B6 projection (reused for Invited SMEs). */
interface PortfolioRow {
  loanReference: string;
  borrowerCui: string;
  borrowerName: string;
  product: string;
  currency: string;
  principal: number;
  outstandingBalance: number;
  interestRate: number;
  maturityDate: string;
  status: string;
  riskGrade: string | null;
  edId: number | null;
  edName: string | null;
}

/**
 * REST-only implementation of {@link LenderProspectsApi} (EM-699 / F4 — EM-709).
 *
 * - **Proposals** (Tab 2) → the live B2 prospect endpoints. The {@code partner}
 *   filter is passed through ({@code all} / undefined omits it). The verdict POST is
 *   keyed by CUI; ROLE_LENDER only on the backend (RM → 403, surfaced via Rule 09).
 * - **Invited SMEs** (Tab 1) → composed from the B6 portfolio endpoint (Rule 10 —
 *   no dedicated endpoint), grouped by the row's real `edId` against the server-resolved channel
 *   catalogue ({@link LENDER_CHANNELS_API}, EM-1613). Before that fix the grouping matched the E-D
 *   name against three demo substrings, so every real partner's SMEs were labelled "Marketplace".
 *   Best-effort: a failed source resolves to an empty list so the tab still renders.
 *
 * REST-only (Rules 07/10). Rides the Keycloak bearer.
 */
@Injectable()
export class LenderProspectsRestService implements LenderProspectsApi {
  private readonly http = inject(HttpClient);
  private readonly channelsApi = inject(LENDER_CHANNELS_API);

  getInvitedSmes(): Observable<InvitedSme[]> {
    const portfolio$ = this.http
      .get<PortfolioRow[]>(API_PATHS.LENDER_SERVICING_PORTFOLIO, { params: new HttpParams().set('size', '1000') })
      .pipe(catchError(() => of<PortfolioRow[]>([])));
    return forkJoin({ rows: portfolio$, channels: this.channelsApi.getChannels() }).pipe(
      map(({ rows, channels }) => (rows ?? []).map(r => this.toInvitedSme(r, channels))),
      catchError(() => of<InvitedSme[]>([])),
    );
  }

  getProposals(partner?: string): Observable<ProspectProposal[]> {
    let params = new HttpParams();
    if (partner && partner !== 'all') {
      params = params.set('partner', partner);
    }
    return this.http
      .get<ProspectQueueItemDto[]>(API_PATHS.LENDER_PROSPECTS, { params })
      .pipe(map(rows => (rows ?? []).map(r => this.toProposal(r))));
  }

  recordVerdict(cui: string, body: ProspectVerdictRequest): Observable<ProspectVerdictResponse> {
    return this.http.post<ProspectVerdictResponse>(
      `${API_PATHS.LENDER_PROSPECTS}/${encodeURIComponent(cui)}/verdict`,
      body,
    );
  }

  // ── Mapping ──────────────────────────────────────────────────────────────────

  private toProposal(r: ProspectQueueItemDto): ProspectProposal {
    return { ...r };
  }

  private toInvitedSme(r: PortfolioRow, channels: LenderChannelOption[]): InvitedSme {
    const match = r.edId == null ? undefined : channels.find(c => c.edId === r.edId);
    const channel = match?.channel ?? channels.find(c => c.general)?.channel ?? 'marketplace';
    const origin = match ? `Partner — ${match.name ?? r.edName ?? ''}` : 'Marketplace';
    return {
      sme: r.borrowerName,
      cui: r.borrowerCui,
      channel,
      origin,
      product: r.product,
      stage: this.stageLabel(r.status),
      amount: r.outstandingBalance,
      currency: r.currency ?? 'EUR',
      status: r.status,
    };
  }

  /** Map a loan status to a human-readable stage label for the Invited-SMEs table. */
  private stageLabel(status: string): string {
    switch (status) {
      case 'ACTIVE':
      case 'RESTRUCTURED':
        return 'Active Loan';
      case 'OVERDUE':
        return 'Repayment — Overdue';
      case 'DEFAULTED':
        return 'Repayment — NPL';
      case 'REPAID':
        return 'Repaid';
      default:
        return status;
    }
  }
}
